import type {
  ChargebeeApplePayButtonOptions,
  ChargebeeComponentsStyle,
  ChargebeeFieldStyles,
  ChargebeeSavePaymentMethodOptions,
} from '@/types/chargebee';

export const CHARGEBEE_SDK_URL = 'https://js.chargebee.com/v2/chargebee.js';
export const CHARGEBEE_SCRIPT_ID = 'chargebee-js-sdk';

/** Mount targets for the Fields-mode card component. */
export const CHARGEBEE_FIELD_IDS = {
  NUMBER: 'cb-card-number',
  EXPIRY: 'cb-card-expiry',
  CVV: 'cb-card-cvv',
} as const;

/** Stripe's default card placeholders, so the two card forms read identically. */
export const CHARGEBEE_FIELD_PLACEHOLDERS = {
  number: '1234 1234 1234 1234',
  expiry: 'MM / YY',
  cvv: 'CVC',
} as const;

/** Mount targets for the standalone Google Pay and Apple Pay handlers. */
export const CHARGEBEE_WALLET_BUTTON_IDS = {
  GOOGLE_PAY: 'cb-gpay-button',
  APPLE_PAY: 'cb-apple-pay-button',
} as const;

/**
 * Apple renders the button itself, so the only choice left is which variant.
 * Black + `plain` matches `ApplePayButtonPlaceholder`, which stays the visible
 * layer (`usePlaceholderAsVisual`) while Apple's button takes the click.
 */
export const CHARGEBEE_APPLE_PAY_BUTTON_OPTIONS: ChargebeeApplePayButtonOptions = {
  buttonColor: 'black',
  buttonType: 'plain',
};

/**
 * What Chargebee's docs have the payment intent declare for the standalone
 * `apple-pay` handler. Our backend creates ONE intent per session for the card
 * form and both wallets, so the Apple Pay mount declares this on its own copy.
 */
export const CHARGEBEE_APPLE_PAY_PAYMENT_METHOD_TYPE = 'apple_pay';

// Chargebee fields render inside a cross-origin iframe, so our CSS can't
// reach them — text styling must be passed here. Values mirror the tokens the
// Stripe/Adyen/Nuvei card forms use (`global.css` @theme):
//   color        --color-card-foreground (#09090b)
//   placeholder  --color-muted-foreground (#71717a)
// 16px matches every other checkout input and also prevents iOS Safari's
// focus-zoom (it zooms any focused field < 16px, ignoring user-scalable).
// The border/radius/height live on OUR wrapper div (`inputStyles`), exactly
// like the Stripe Elements wrappers. `fontFamily` can't be `inherit` across the
// iframe boundary, so pass the app's own declaration verbatim — same string as
// `global.css` `body` and `APPCHARGE_PAYMENT_ELEMENT_FIELD_STYLES`.
export const CHARGEBEE_FIELD_STYLES: ChargebeeFieldStyles = {
  base: {
    fontSize: '16px',
    // Unlike Stripe (whose iframe fills the wrapper, so a tall line box is what
    // centers the text), Chargebee shrink-wraps each iframe to the line box and
    // pins it to the top of the wrapper. A tall line box here would only pad the
    // iframe and push the text further up — the wrapper centers it instead
    // (`justify-center` on the mount host in ChargebeeCardForm).
    lineHeight: '24px',
    color: '#09090b',
    fontFamily: 'font-sans, sans-serif',
    fontSmoothing: 'antialiased',
    '::placeholder': { color: '#71717a' },
  },
  // Invalid state keeps normal text color — the error is signalled by the
  // red border on our wrapper + the error line below it, matching Stripe.
  invalid: { color: '#09090b' },
};

/**
 * Mount target for the Payment Component that serves the long tail of APMs.
 * Must NOT be `cb-payment-component`: that is the id/name Chargebee.js gives
 * the iframe it injects, and it looks that id up by `getElementById` — a host
 * carrying the same id shadows the iframe and `mount()` never settles.
 */
export const CHARGEBEE_PAYMENT_COMPONENT_ID = 'appcharge-chargebee-payment-component-host';
/** Host for Chargebee's `payment-button` component that drives the one above. */
export const CHARGEBEE_PAYMENT_BUTTON_ID = 'appcharge-chargebee-payment-button-host';

/**
 * Routed APM rows are Stripe rows, so `pspPaymentMethodName` is Stripe's
 * `payment_method_types` string. Chargebee's Payment Component wants ITS
 * `payment_method_type`; the two agree for every Korean method but cards.
 * Anything not listed passes through unchanged.
 */
export const CHARGEBEE_PAYMENT_METHOD_TYPE_BY_PSP_NAME: Readonly<Record<string, string>> = {
  kr_card: 'south_korean_cards',
};

/**
 * Chargebee renders the drop-in's own "I authorize … to save this payment
 * method" block whenever the consent label is non-empty. Ours is replaced by
 * the legal-approved renewal disclaimer (`SubscriptionDisclaimer`, ACDEV-26078);
 * Chargebee keeps vaulting the method for renewals regardless of this consent.
 */
export const CHARGEBEE_PAYMENT_COMPONENT_SAVE_OPTIONS: ChargebeeSavePaymentMethodOptions = {
  label: '',
  consentType: 'consent',
  description: '',
  defaultChecked: false,
};

/**
 * The drop-in paints inside its own iframe, so the checkout's tokens have to be
 * handed over as Chargebee theme variables. Values mirror `global.css` @theme
 * and the Fields-mode `CHARGEBEE_FIELD_STYLES` so the two Chargebee surfaces
 * read as one form. `section` padding is zeroed because our wrapper owns the
 * gutters (same as every other PSP drop-in in the payment column).
 */
export const CHARGEBEE_PAYMENT_COMPONENT_STYLE: ChargebeeComponentsStyle = {
  theme: { radius: 'medium', scaling: '100%' },
  variables: {
    colorBackground: '#ffffff',
    gray12: '#09090b',
    defaultFontFamily: 'font-sans, sans-serif',
  },
  rules: {
    section: { padding: '0px' },
  },
};

/**
 * Boot-time hydration of the `{ id }`-only intent. A plain read shouldn't cost
 * the shopper the wallet buttons for the whole session, so a blip is retried
 * (linear backoff) before the wallets collapse as unavailable.
 */
export const CHARGEBEE_INTENT_RETRIEVAL_ATTEMPTS = 3;
export const CHARGEBEE_INTENT_RETRIEVAL_RETRY_DELAY_MS = 500;

/** Subscription status poll cadence (ported from v2). */
export const SUBSCRIPTION_POLLING_INTERVAL_MS = 2500;

/**
 * How long network failures are tolerated before the poll gives up. NOT an
 * overall polling cutoff: the status endpoint is a plain read with no BE-side
 * timeout, so a reachable backend keeps being polled (same contract as
 * {@link useOrderPolling}). Ported from v2's `MINUTE_IN_MILLISECONDS`.
 */
export const SUBSCRIPTION_POLLING_CONNECTION_LOST_GRACE_MS = 60_000;

/** Backend error code returned when the customer already has a live subscription. */
export const DUPLICATE_SUBSCRIPTION_ERROR_CODE = 'DUPLICATE_SUBSCRIPTION';
